import React, { useState, useEffect, useRef } from "react";
import { Box, Skeleton, Flex, Popover, Button } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconPencil, IconX } from "@tabler/icons";
import { saveIdea } from "../../../../Api";
import { useAuth0 } from "@auth0/auth0-react";
import { getNestedFieldValue } from "../../../ideaNiv/helpers/calculateImpactResults";
import { useUserPermissions } from "../../../../hooks/useUserPermissions";
import moment from "moment";

// Sentinel to distinguish "never saved" from "intentionally saved null (cleared)"
const NEVER_SAVED = "__NEVER_SAVED__";

export const EditableDateCell: React.FC<any> = ({ data, col }) => {
  const auth0 = useAuth0();
  const { canEditIdeasInline } = useUserPermissions();
  const [dateValue, setDateValue] = useState<Date | null>(null);
  const [initialDate, setInitialDate] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [opened, setOpened] = useState(false);
  const lastSavedValueRef = useRef<string | null>(NEVER_SAVED);
  const isInitialMountRef = useRef(true);
  const isEditable = canEditIdeasInline;
  // Initialize date value from data - preserve local state after saving
  useEffect(() => {
    const rawValue = getNestedFieldValue(data, col.accessor);
    let newDate: Date | null = null;
    let newDateISO: string | null = null;
    
    if (rawValue) {
      const date = new Date(rawValue);
      if (!isNaN(date.getTime())) {
        newDate = date;
        newDateISO = date.toISOString();
      }
    }

    // On initial mount, always set the value
    if (isInitialMountRef.current) {
      setDateValue(newDate);
      setInitialDate(newDate);
      // Only record the initial value when a date actually exists.
      // Leaving lastSavedValueRef as NEVER_SAVED for empty cells ensures
      // the external-sync condition on line 62 can still fire if the backend
      // later provides a date for a cell that mounted without one.
      if (newDateISO !== null) {
        lastSavedValueRef.current = newDateISO;
      }
      isInitialMountRef.current = false;
      return;
    }

    // After initial mount:
    // - If we have a saved value, only sync if data matches what we saved (backend confirmed)
    // - Otherwise, if data has a value and we don't, and we have never saved, sync it
    // - This preserves local state after saving until backend confirms
    setDateValue((current) => {
      const currentISO = current ? current.toISOString() : null;
      
      // If data matches what we last saved, sync it (backend confirmed)
      if (newDateISO === lastSavedValueRef.current && currentISO !== newDateISO) {
        setInitialDate(newDate);
        return newDate;
      }
      
      // Only sync incoming data if we have never saved anything in this session.
      // Using NEVER_SAVED sentinel prevents confusing "cleared" (null) with "never saved" (NEVER_SAVED).
      if (currentISO === null && newDateISO !== null && lastSavedValueRef.current === NEVER_SAVED) {
        setInitialDate(newDate);
        lastSavedValueRef.current = newDateISO;
        return newDate;
      }
      
      // Otherwise, preserve current state
      return current;
    });
  }, [data, col.accessor]);

  const handleDateChange = async (date: Date | null) => {
    setDateValue(date);
    setOpened(false);

    // Only save if the date has actually changed
    const hasChanged = 
      (date === null && initialDate !== null) ||
      (date !== null && initialDate === null) ||
      (date !== null && initialDate !== null && date.getTime() !== initialDate.getTime());

    if (hasChanged && !isSaving) {
      setIsSaving(true);
      try {
        // Format date as ISO string for backend
        const dateToSave = date ? date.toISOString() : null;
        await saveIdea({ id: data._id, [col.accessor]: dateToSave, extraFields: true }, auth0);
        setInitialDate(date);
        // Update the ref to track the saved value - this prevents resetting on re-render
        lastSavedValueRef.current = dateToSave;
      } catch (error) {
        console.error("Error updating idea date:", error);
        // Revert to initial date on error
        setDateValue(initialDate);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleOpenDatePicker = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpened(true);
  };

  // Format date for display - use format from renderParams or default
  const dateFormat = col.renderParams?.format || "MMMM DD, YYYY";
  const formattedDate = dateValue 
    ? moment(dateValue).format(dateFormat)
    : "No Date Available";
  // If not editable, render as read-only
  if (!isEditable) {
    return (
      <Box sx={{ cursor: "default" }}>{formattedDate}</Box>
    );
  }
  return (
    <Box
      p={0}
      m={0}
      h="100%"
      w="100%"
      onMouseMove={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      sx={{
        "&:hover .pencil-icon": {
          opacity: 1,
        },
      }}
    >
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom"
        withArrow
        withinPortal
        trapFocus
      >
        <Popover.Target>
          <Flex 
            align="center" 
            justify="space-between" 
            h="100%"
          >
            <Box
              sx={{
                flex: 1,
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: "6px",
                fontSize: "0.825rem",
                color: !dateValue ? "#6c757d" : "inherit",
                opacity: !dateValue ? 0.6 : 1,
                "&:hover": {
                  backgroundColor: "rgba(91, 96, 227, 0.08)",
                },
              }}
              onClick={handleOpenDatePicker}
            >
              {formattedDate}
            </Box>
            <Box
              className="pencil-icon"
              sx={{
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                marginRight: "8px",
                opacity: 0,
                transition: "opacity 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(70, 76, 243, 0.05)",
                  borderRadius: "4px",
                },
              }}
              onClick={handleOpenDatePicker}
            >
              <IconPencil size={14} color="#5B60E3" />
            </Box>
          </Flex>
        </Popover.Target>
        <Popover.Dropdown
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <DatePicker
            value={dateValue}
            onChange={handleDateChange}
            firstDayOfWeek={0}
          />
          <Box
            sx={{
              marginTop: "8px",
              paddingTop: "8px",
              borderTop: "1px solid #E9ECEF",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {dateValue && (
              <Button
                onClick={() => handleDateChange(null)}
                size="xs"
                variant="subtle"
                color="red"
                styles={{
                  root: {
                    height: 28,
                    fontSize: 12,
                    borderRadius: "4px",
                  },
                }}
              >
                Clear date
              </Button>
            )}
            <Box sx={{ marginLeft: "auto" }}>
              <Button
                onClick={() => setOpened(false)}
                size="xs"
                leftIcon={<IconX size={14} />}
                styles={(theme) => ({
                  root: {
                    backgroundColor: "#5B60E3",
                    border: 0,
                    height: 28,
                    fontSize: 12,
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: theme.fn.darken("#5B60E3", 0.05),
                    },
                  },
                })}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
};

// Skeleton component for EditableDateCell
export const EditableDateCellSkeleton: React.FC<{ col: any }> = ({ col }) => {
  const columnWidth = col.width;
  const minWidth = col.minWidth;
  const maxWidth = col.maxWidth;
  
  // Date cells typically have a fixed width
  const dateWidth = columnWidth 
    ? Math.min(parseInt(columnWidth) * 0.7, 150)
    : 120;
  
  return (
    <Box 
      sx={{ 
        width: columnWidth || "100%", 
        minWidth, 
        maxWidth 
      }}
    >
      <Skeleton height={16} width={dateWidth} radius="sm" />
    </Box>
  );
};

